<script setup lang="ts">
import {CallToActionSettings} from "@/SocialWidget/types/call_to_action_types";
import {computed, ref, watch} from "vue";
import {isEmpty2} from "@/utils";

const props = defineProps<{
    settings: CallToActionSettings,
    logo?: File | null | undefined,
    bgImage?: File | null | undefined,
    bgLogo?: File | null | undefined,
    endpoint?: string,
    widgetId?: string,
}>();

defineEmits(['openModal', 'close']);

const uploadedBgImage = computed(() => {
    if (!isEmpty2(props.bgImage)) {
        return URL.createObjectURL(props.bgImage!);
    }
    return '';
});

const roundStyle = () => {
    if (props.settings.widget_rounded === 'none') {
        return { borderRadius: '0'}
    }
    if (props.settings.widget_rounded === 'standard') {
        return { borderRadius: '8px'}
    }
    if (props.settings.widget_rounded === 'round') {
        return { borderRadius: '24px'}
    }
}
const shadowStyle = () => {
    if (props.settings.widget_theme === 'light' && props.settings.widget_bg_type === 'full') {
        return { boxShadow: 'white -10px 0 15px 15px'}
    }
    if (props.settings.widget_theme === 'dark' && props.settings.widget_bg_type === 'full') {
        return { boxShadow: '#0C1B20 -10px 0 15px 15px'}
    }
}
const isMobile = !window.matchMedia('(max-width: 414px)').matches;
</script>

<template>
    <div class="relative grid grid-cols-4 items-center overflow-hidden dg-shadow-widget"
         :class="[settings.widget_theme === 'dark' ? 'bg-[#0C1B20]' : 'bg-[#FFFFFF]',
         isMobile ? 'max-w-[300px]' : 'max-w-[260px]',
         ]"
         :style="[roundStyle(),`color: ${settings.widget_text_color}`]">
        <button class="absolute z-30"
                @click="$emit('close')"
                :class="[props.settings.widget_rounded === 'round' ? 'top-[4px] right-[8px]' : 'top-[3px] right-[3px]']">
            <svg width="12" height="12" viewBox="0 0 14 13" :fill="settings.widget_text_color" opacity="0.8" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M11.0833 3.68101L10.2608 2.94788L6.99996 5.85442L3.73913 2.94788L2.91663 3.68101L6.17746 6.58756L2.91663 9.4941L3.73913 10.2272L6.99996 7.32069L10.2608 10.2272L11.0833 9.4941L7.82246 6.58756L11.0833 3.68101Z"
                />
            </svg>
        </button>
        <button type="button" @click="$emit('openModal')"
                class="font-semibold p-[8px] rounded-full flex left-[16px] items-center justify-center size-[32px] absolute z-30"
                :style="[`background: ${settings.widget_button_color}`]">
            <svg width="16" height="16" :fill="settings.widget_button_text_color" viewBox="0 0 1500 1500" xmlns="http://www.w3.org/2000/svg">
                <g>
                    <path d="m1261.1 516.7-92.6 92.6-352.7-352.7v1237.4h-130.9v-1237.4l-353.4 353.4-92.6-92.6 511.4-511.4z">
                    </path>
                </g>
            </svg>
        </button>

        <div class="relative z-20 col-span-1 h-full flex items-center justify-center">
            <div class="absolute top-0 left-0 w-full h-full z-10 "
                 v-if="settings.widget_bg_type === 'full'"
                 :style="[roundStyle(), `border-top-right-radius: 0px; border-bottom-right-radius: 0px;`]"
                 :class="[settings.widget_theme === 'dark' ? 'opacity-85' : '',]">
                <img v-if="!isEmpty2(props.bgImage) || !settings.widget_bg_image_url"
                     :src="uploadedBgImage"
                     class="object-cover w-full h-full rounded-l-md"
                     :style="[roundStyle(), `border-top-right-radius: 0px; border-bottom-right-radius: 0px; border-bottom-right-radius: 0px;`]">
                <img v-if="settings.widget_bg_image_url && isEmpty2(props.bgImage)"
                     :src="settings.widget_bg_image_url"
                     alt="gift"
                     class="object-cover w-full h-full rounded-l-md"
                     :style="[roundStyle(), `border-top-right-radius: 0px; border-bottom-right-radius: 0px; border-bottom-right-radius: 0px;`]">
            </div>
        </div>
        <div class="font-normal col-span-3 relative z-20 text-left text-[14px] py-[8px] pe-[16px]" :style="[shadowStyle()]">
            {{settings.widget_title}}
        </div>
    </div>
</template>

<style scoped>

</style>
